import { Html } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSpring, animated as a, config } from "@react-spring/web";
import { useWindowSize } from "react-use";

import { Quaternion, Vector3 } from "three";

const useWindowScale = () => {
  const { width } = useWindowSize();

  const scale = useMemo(() => {
    return width / 1920;
  }, [width]);

  return scale;
};

export const MarkerLabel = ({ node, idx = 0 }) => {
  const scale = useWindowScale();
  const [flipped, setFlipped] = useState(false);
  const { t } = useTranslation();
  const { camera } = useThree();
  const camForward = useMemo(() => new Vector3());

  const isLeftOrientation = node.userData.left !== undefined;
  const isBottomOrientation = node.userData.bottom !== undefined;

  const xFactor = isLeftOrientation ? -1 : 1;
  const yFactor = isBottomOrientation ? -1 : 1;

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 350 + 150 * idx,
    config: config.molasses,
  });

  const markerLeftDirection = useMemo(() => {
    let left = new Vector3(-1, 0, 0);
    const worldRotation = new Quaternion();
    node.getWorldQuaternion(worldRotation);
    left.applyQuaternion(worldRotation);
    return left;
  }, []);

  useFrame(() => {
    camera.getWorldDirection(camForward);
    camForward.y = 0;
    camForward.normalize();
    // var dot = camForward.dot(lineDirection);
    // var a = dot + 1.05;
    // a = THREE.MathUtils.clamp(a, 0, 1);
    // var r = THREE.MathUtils.clamp(a, 0.3, 1);

    var l = camForward.cross(markerLeftDirection).y < 0;
    if (l != flipped) setFlipped(l);
  }, []);

  const left = useMemo(() => {
    if (isLeftOrientation) {
      return !flipped;
    } else {
      return flipped;
    }
  }, [flipped]);

  return (
    <group position={node.position}>
      <Html
        zIndexRange={[1, 0]}
        style={{
          pointerEvents: "none",
        }}
      >
        <a.div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "left top",
            opacity: opacity,
          }}
        >
          <div
            style={{
              position: "absolute",
              ...(left ? { left: `${130 * -node.scale.x - 230}px` } : { left: `${130 * node.scale.x}px` }),
              ...(isBottomOrientation ? { top: `${125 * node.scale.y}px` } : { top: `${-130 * node.scale.y - 25}px` }),
              color: "#fff",
              // fontWeight: "400",
              fontSize: "18px",
              textDecoration: "none",
              textWrap: "wrap",
              width: "230px",
              textAlign: left ? "right" : "left",
            }}
          >
            {t(node.userData.textId)}
          </div>
          <div
            style={{
              position: "relative",
              transform: left
                ? isBottomOrientation
                  ? "translate(-100%,-5%)"
                  : "translate(-100%,-95%)"
                : isBottomOrientation
                  ? "translate(0,-5%)"
                  : "translate(0,-95%)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={260 * node.scale.x}
              height={130 * node.scale.y}
              //viewBox={`0 0 ${190 * node.scale.x} ${130 * node.scale.y}`}
              style={{
                transform: `scale(${xFactor},${yFactor})`,
              }}
            >
              <path
                d={`M-0,${130 * node.scale.y}l${125 * node.scale.x}-${125 * node.scale.y}h${125 * node.scale.x}`}
                fill="none"
                stroke="#fff"
                opacity={0.4}
                strokeWidth="3"
              />
            </svg>
          </div>
        </a.div>
      </Html>
    </group>
  );
};

export const MarkerLabels = ({ node }) => {
  const markers = useMemo(() => {
    const mrks = [];
    node.traverse((c) => {
      if (c.name.startsWith("marker_")) {
        mrks.push(c);
      }
    });

    return mrks;
  }, []);

  return (
    <group>
      {markers.map((m, i) => (
        <MarkerLabel node={m} key={`${node.name}_marker_${i}`} idx={i} />
      ))}
    </group>
  );
};
